import type {
  Evenement,
  EvenementAtelier,
  EvenementConference,
  EvenementConferencier,
  EvenementMasterclass,
} from '@mtc/shared';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AfficheThumbnail } from '../components/AfficheThumbnail';
import { ConferencierModal } from '../components/ConferencierModal';
import { ImgPlaceholder } from '../components/ImgPlaceholder';
import { StateMessage } from '../components/StateMessage';
import { useEvenement } from '../data/queries';
import { useSEO } from '../hooks/useSEO';
import { trackEvenementInscriptionClick } from '../lib/track';

/**
 * Anchor "session item" — atelier OR masterclass. They share a structure
 * (time-slotted, paid, seats, speakers, affiche, programme PDF) and are
 * grouped/exclusive together in the public selection panel.
 */
type SessionItem = (EvenementAtelier | EvenementMasterclass) & {
  kind: 'atelier' | 'masterclass';
};

const CATEGORY_LABEL: Record<Evenement['category'], string> = {
  congres: 'Congrès',
  symposium: 'Symposium',
  workshop: 'Workshop',
  webinaire: 'Webinaire',
  autre: 'Événement',
};

export function EvenementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const eventQ = useEvenement(id);
  const [openConferencier, setOpenConferencier] = useState<EvenementConferencier | null>(null);

  useSEO({
    title: eventQ.data ? `${eventQ.data.title} — MTC` : 'Événement — MTC',
    description: eventQ.data?.shortDescription ?? '',
    schemaOrg: eventQ.data
      ? {
          '@context': 'https://schema.org',
          '@type': 'Event',
          name: eventQ.data.title,
          description: eventQ.data.shortDescription,
          startDate: eventQ.data.startDate,
          endDate: eventQ.data.endDate,
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode:
            eventQ.data.category === 'webinaire'
              ? 'https://schema.org/OnlineEventAttendanceMode'
              : 'https://schema.org/OfflineEventAttendanceMode',
          location: {
            '@type': 'Place',
            name: eventQ.data.venue || eventQ.data.city,
            address: {
              '@type': 'PostalAddress',
              addressLocality: eventQ.data.city,
              addressCountry: eventQ.data.country,
            },
          },
          organizer: {
            '@type': 'Organization',
            name: eventQ.data.organizer,
            url: eventQ.data.organizerWebsite ?? undefined,
          },
          image: eventQ.data.image ?? undefined,
          url: eventQ.data.registrationUrl ?? undefined,
        }
      : undefined,
  });

  if (eventQ.isLoading) return <StateMessage variant="loading" />;
  if (eventQ.error) return <StateMessage variant="error" message={String(eventQ.error)} />;
  const event = eventQ.data;
  if (!event) return <StateMessage variant="empty" message="Événement introuvable." />;

  return (
    <div style={{ padding: '48px 32px 96px', maxWidth: 1100, margin: '0 auto' }}>
      <button
        type="button"
        className="mtc-back"
        onClick={() => navigate('/evenements')}
      >
        ← Tous les événements
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 32, marginTop: 24 }}>
        <div>
          <div
            style={{
              fontSize: 11,
              fontFamily: 'var(--mtc-font-mono)',
              letterSpacing: '0.2em',
              textTransform: 'uppercase',
              color: 'var(--mtc-primary, #0B5D3B)',
              marginBottom: 12,
            }}
          >
            {CATEGORY_LABEL[event.category]}
            {event.subtitle ? ` · ${event.subtitle}` : ''}
          </div>
          <h1
            style={{
              fontFamily: 'var(--mtc-font-display)',
              fontSize: 'clamp(36px, 4.5vw, 64px)',
              fontWeight: 400,
              lineHeight: 1.0,
              letterSpacing: '-0.035em',
              margin: '0 0 16px',
              textWrap: 'balance',
            }}
          >
            {event.title}
          </h1>
          <p
            style={{
              fontSize: 'clamp(16px, 1.3vw, 19px)',
              lineHeight: 1.5,
              color: 'rgba(15,25,22,0.7)',
              margin: 0,
              maxWidth: 720,
            }}
          >
            {event.shortDescription}
          </p>
        </div>

        {event.image && (
          <ImgPlaceholder
            label={event.title}
            ratio="16/9"
            tone="vert"
            src={event.image}
            alt={event.title}
            fit="cover"
          />
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: 24,
            padding: '24px 28px',
            background: 'rgba(11,93,59,0.04)',
            border: '0.5px solid rgba(11,93,59,0.15)',
            borderRadius: 12,
          }}
        >
          <InfoBlock label="Dates" value={formatDateRange(event.startDate, event.endDate)} />
          <InfoBlock
            label="Lieu"
            value={`${event.venue ? event.venue + ' — ' : ''}${event.city}${
              event.country && event.country !== 'Maroc' ? `, ${event.country}` : ''
            }`}
          />
          <InfoBlock label="Organisateur" value={event.organizer} />
          {event.registrationInfo && (
            <InfoBlock label="Inscription" value={event.registrationInfo} />
          )}
        </div>

        {event.description && (
          <section>
            <h2
              style={{
                fontFamily: 'var(--mtc-font-mono)',
                fontSize: 11,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'rgba(15,25,22,0.55)',
                marginBottom: 12,
              }}
            >
              Présentation
            </h2>
            <div
              style={{
                fontSize: 16,
                lineHeight: 1.7,
                color: 'rgba(15,25,22,0.85)',
                whiteSpace: 'pre-wrap',
                maxWidth: 720,
              }}
            >
              {event.description}
            </div>
          </section>
        )}

        {event.topics.length > 0 && (
          <section>
            <h2
              style={{
                fontFamily: 'var(--mtc-font-mono)',
                fontSize: 11,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: 'rgba(15,25,22,0.55)',
                marginBottom: 12,
              }}
            >
              Thématiques
            </h2>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {event.topics.map((t) => (
                <span
                  key={t}
                  style={{
                    fontSize: 12,
                    padding: '6px 12px',
                    borderRadius: 999,
                    background: 'rgba(11,93,59,0.08)',
                    color: 'var(--mtc-primary, #0B5D3B)',
                  }}
                >
                  {t}
                </span>
              ))}
            </div>
          </section>
        )}

        {event.registrationMode === 'hosted-mtc' && event.hostedRegistration && (
          <>
            <ConferenciersSection
              conferenciers={event.hostedRegistration.conferenciers ?? []}
              onOpen={setOpenConferencier}
            />
            <ProgrammeSection
              conferences={event.hostedRegistration.conferences ?? []}
              ateliers={event.hostedRegistration.ateliers ?? []}
              masterclasses={event.hostedRegistration.masterclasses ?? []}
              conferenciersById={Object.fromEntries(
                (event.hostedRegistration.conferenciers ?? []).map((c) => [c.id, c]),
              )}
              onOpenConferencier={setOpenConferencier}
            />
          </>
        )}

        {event.registrationMode === 'hosted-mtc' && event.hostedRegistration ? (
          <HostedRegistrationPanel event={event} />
        ) : (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
            {event.registrationUrl && (
              <a
                href={event.registrationUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mtc-btn mtc-btn-primary"
                onClick={() => trackEvenementInscriptionClick(event.slug)}
              >
                S'inscrire à l'événement ↗
              </a>
            )}
            {event.programUrl && (
              <a
                href={event.programUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mtc-btn mtc-btn-ghost"
              >
                Voir le programme ↗
              </a>
            )}
            {event.organizerWebsite && (
              <a
                href={event.organizerWebsite}
                target="_blank"
                rel="noopener noreferrer"
                className="mtc-btn mtc-btn-ghost"
              >
                Site de l'organisateur ↗
              </a>
            )}
          </div>
        )}
      </div>

      <ConferencierModal
        conferencier={openConferencier}
        onClose={() => setOpenConferencier(null)}
      />
    </div>
  );
}

function fmtAtelierTime(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime())) return '';
  const dayLabel = start.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
  const timeFmt = (d: Date) =>
    d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (Number.isNaN(end.getTime())) return `${dayLabel} · ${timeFmt(start)}`;
  return `${dayLabel} · ${timeFmt(start)}–${timeFmt(end)}`;
}

function HostedRegistrationPanel({ event }: { event: Evenement }) {
  const navigate = useNavigate();
  const hosted = event.hostedRegistration!;
  const general = hosted.generalInscription;
  const ateliers = hosted.ateliers ?? [];
  const masterclasses = hosted.masterclasses ?? [];
  const packs = hosted.packs ?? [];

  return (
    <section
      style={{
        marginTop: 12,
        padding: '24px 28px',
        background: '#fff',
        border: '1px solid rgba(11,93,59,0.2)',
        borderRadius: 12,
        boxShadow: '0 4px 24px rgba(11,93,59,0.05)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 11,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: 'var(--mtc-primary, #0B5D3B)',
          marginBottom: 12,
        }}
      >
        Inscription
        {event.partnerName ? ` · Géré par MTC pour ${event.partnerName}` : ' · Géré par MTC'}
      </div>

      <h2 style={{ fontSize: 20, fontWeight: 600, margin: '0 0 16px', color: '#0F1916' }}>
        S'inscrire à « {event.title} »
      </h2>

      {/* Packs preview — shown BEFORE the selection so visitors see the deals
          up front. Informational here (audience is declared on the next step,
          where a pack is actually applied). */}
      {packs.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontFamily: 'var(--mtc-font-mono)',
              fontSize: 11,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--mtc-primary, #0B5D3B)',
              marginBottom: 10,
            }}
          >
            Formules avantageuses
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {packs.map((p) => {
              const audienceLabel =
                ({
                  all: 'Tous publics',
                  etudiant: 'Étudiant·e',
                  'jeune-diplome': 'Jeune diplômé·e',
                  praticien: 'Praticien·ne',
                } as Record<string, string>)[p.audience] ?? p.audience;
              const inclA = ateliers.filter((a) => p.includes.atelierIds.includes(a.id));
              const inclM = masterclasses.filter((m) => p.includes.masterclassIds.includes(m.id));
              let nominal = p.includes.general ? general.price ?? 0 : 0;
              for (const a of inclA) nominal += a.price ?? 0;
              for (const m of inclM) nominal += m.price ?? 0;
              const save = nominal - (p.price ?? 0);
              const contents = [
                p.includes.general ? 'Inscription générale' : null,
                inclA.length ? `${inclA.length} atelier${inclA.length > 1 ? 's' : ''}` : null,
                inclM.length
                  ? `${inclM.length} masterclass${inclM.length > 1 ? 'es' : ''}`
                  : null,
              ]
                .filter(Boolean)
                .join(' + ');
              return (
                <div
                  key={p.id}
                  style={{
                    border: '1px solid rgba(11,93,59,0.18)',
                    borderRadius: 10,
                    padding: '12px 14px',
                    background: 'rgba(11,93,59,0.03)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                      alignItems: 'baseline',
                    }}
                  >
                    <strong style={{ fontSize: 15, color: '#0F1916' }}>{p.name}</strong>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: 'var(--mtc-primary, #0B5D3B)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {save > 0 && (
                        <span
                          style={{
                            textDecoration: 'line-through',
                            color: 'rgba(15,25,22,0.4)',
                            fontWeight: 400,
                            marginRight: 6,
                          }}
                        >
                          {nominal.toLocaleString('fr-FR')}
                        </span>
                      )}
                      {(p.price ?? 0).toLocaleString('fr-FR')} MAD
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(15,25,22,0.6)', marginTop: 3 }}>
                    {audienceLabel}
                    {contents ? ` · ${contents}` : ''}
                  </div>
                  {(p.discountPercent ?? 0) > 0 && (p.discountAudiences ?? []).length > 0 && (
                    <div style={{ fontSize: 11.5, fontWeight: 600, color: '#9E7508', marginTop: 5 }}>
                      −{p.discountPercent}% pour{' '}
                      {(p.discountAudiences ?? [])
                        .map(
                          (a) =>
                            (
                              {
                                all: 'tous',
                                etudiant: 'étudiants',
                                'jeune-diplome': 'jeunes diplômés',
                                praticien: 'praticiens',
                              } as Record<string, string>
                            )[a] ?? a,
                        )
                        .join(' & ')}
                    </div>
                  )}
                  {p.description && (
                    <div style={{ fontSize: 12.5, color: 'rgba(15,25,22,0.72)', marginTop: 6 }}>
                      {p.description}
                    </div>
                  )}
                  {save > 0 && (
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: 'var(--mtc-primary, #0B5D3B)',
                        marginTop: 6,
                      }}
                    >
                      Économisez {save.toLocaleString('fr-FR')} MAD
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 12, color: 'rgba(15,25,22,0.5)', marginTop: 8 }}>
            Appliquez un pack à l'étape d'inscription, selon votre statut.
          </div>
        </div>
      )}

      <div style={{ fontSize: 13.5, color: 'rgba(15,25,22,0.7)', lineHeight: 1.6, margin: '4px 0 18px' }}>
        Choisissez votre formule — inscription générale, ateliers et masterclasses — puis vos
        informations et le paiement, sur une seule page. Inscription et paiement gérés par Moroccan
        Training Center.
      </div>

      <button
        type="button"
        className="mtc-btn mtc-btn-primary mtc-btn-lg"
        onClick={() => {
          trackEvenementInscriptionClick(event.slug);
          navigate(`/evenements/${event.id}/inscription`);
        }}
      >
        S'inscrire à l'événement →
      </button>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Conférenciers section — grid of speaker cards above the registration panel
// ─────────────────────────────────────────────────────────────────────────
function ConferenciersSection({
  conferenciers,
  onOpen,
}: {
  conferenciers: ReadonlyArray<EvenementConferencier>;
  onOpen: (c: EvenementConferencier) => void;
}) {
  if (conferenciers.length === 0) return null;
  return (
    <section style={{ marginTop: 8 }}>
      <h2
        style={{
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 11,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: 'rgba(15,25,22,0.55)',
          marginBottom: 16,
        }}
      >
        Conférenciers du congrès
      </h2>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 14,
        }}
      >
        {conferenciers.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onOpen(c)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
              padding: '18px 12px',
              border: '1px solid rgba(15,25,22,0.08)',
              borderRadius: 12,
              background: 'white',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.borderColor = 'var(--mtc-primary, #0B5D3B)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(11,93,59,0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = 'rgba(15,25,22,0.08)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {c.photoUrl ? (
              <img
                src={c.photoUrl}
                alt={c.name}
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '2px solid rgba(11,93,59,0.15)',
                }}
              />
            ) : (
              <div
                style={{
                  width: 96,
                  height: 96,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0B5D3B 0%, #094A30 100%)',
                  color: 'white',
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 32,
                  fontFamily: 'var(--mtc-font-display)',
                }}
              >
                {(c.name?.[0] ?? '?').toUpperCase()}
              </div>
            )}
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0F1916', lineHeight: 1.25 }}>
                {c.name}
              </div>
              {c.title && (
                <div
                  style={{
                    fontSize: 12,
                    color: 'rgba(15,25,22,0.6)',
                    marginTop: 4,
                    lineHeight: 1.35,
                  }}
                >
                  {c.title}
                </div>
              )}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Programme — conferences (free, included in general inscription)
// ─────────────────────────────────────────────────────────────────────────
function ProgrammeSection({
  conferences,
  ateliers,
  masterclasses,
  conferenciersById,
  onOpenConferencier,
}: {
  conferences: ReadonlyArray<EvenementConference>;
  ateliers: ReadonlyArray<EvenementAtelier>;
  masterclasses: ReadonlyArray<EvenementMasterclass>;
  conferenciersById: Record<string, EvenementConferencier>;
  onOpenConferencier: (c: EvenementConferencier) => void;
}) {
  const sortedConfs = [...conferences].sort((a, b) =>
    (a.startDateTime ?? '').localeCompare(b.startDateTime ?? ''),
  );
  const sessions: SessionItem[] = [
    ...ateliers.map((a) => ({ ...a, kind: 'atelier' as const })),
    ...masterclasses.map((m) => ({ ...m, kind: 'masterclass' as const })),
  ].sort((a, b) => (a.startDateTime ?? '').localeCompare(b.startDateTime ?? ''));

  const hasConfs = sortedConfs.length > 0;
  const hasSessions = sessions.length > 0;
  const [tab, setTab] = useState<'conferences' | 'sessions'>(
    hasConfs ? 'conferences' : 'sessions',
  );
  if (!hasConfs && !hasSessions) return null;

  const tabs = [
    hasConfs ? { id: 'conferences' as const, label: 'Conférences' } : null,
    hasSessions ? { id: 'sessions' as const, label: 'Ateliers & masterclasses' } : null,
  ].filter((t): t is { id: 'conferences' | 'sessions'; label: string } => t !== null);

  const speakersOf = (ids: ReadonlyArray<string> | undefined) =>
    (ids ?? [])
      .map((id) => conferenciersById[id])
      .filter((c): c is EvenementConferencier => Boolean(c));

  return (
    <section style={{ marginTop: 8 }}>
      <div
        style={{
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 11,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          color: 'rgba(15,25,22,0.55)',
          marginBottom: 12,
        }}
      >
        Programme
      </div>

      <div
        role="tablist"
        aria-label="Programme de l'événement"
        style={{
          display: 'flex',
          gap: 8,
          borderBottom: '1px solid rgba(15,25,22,0.1)',
          marginBottom: 18,
        }}
      >
        {tabs.map((t) => {
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              style={{
                appearance: 'none',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '11px 4px',
                marginBottom: -1,
                fontFamily: 'var(--mtc-font-mono)',
                fontSize: 12,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: active ? 'var(--mtc-primary, #0B5D3B)' : 'rgba(15,25,22,0.5)',
                borderBottom: `2px solid ${active ? 'var(--mtc-primary, #0B5D3B)' : 'transparent'}`,
                transition: 'color 0.15s ease',
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'conferences' && hasConfs && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12.5, color: 'rgba(15,25,22,0.6)', margin: '-4px 0 4px' }}>
            Incluses dans l'inscription générale.
          </div>
          {sortedConfs.map((conf) => (
            <ProgrammeCard
              key={conf.id}
              afficheUrl={conf.afficheUrl}
              title={conf.title}
              timeLabel={fmtAtelierTime(conf.startDateTime, conf.endDateTime)}
              description={conf.description}
              speakers={speakersOf(conf.conferencierIds)}
              onOpenConferencier={onOpenConferencier}
            />
          ))}
        </div>
      )}

      {tab === 'sessions' && hasSessions && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 12.5, color: 'rgba(15,25,22,0.6)', margin: '-4px 0 4px' }}>
            En option — à choisir lors de l'inscription.
          </div>
          {sessions.map((s) => (
            <ProgrammeCard
              key={s.id}
              afficheUrl={s.afficheUrl}
              badge={s.kind === 'masterclass' ? 'Masterclass' : undefined}
              title={s.title}
              timeLabel={fmtAtelierTime(s.startDateTime, s.endDateTime)}
              description={s.description}
              speakers={speakersOf(s.conferencierIds)}
              priceLabel={s.price > 0 ? `${Math.round(s.price).toLocaleString('fr-FR')} MAD` : 'Gratuit'}
              onOpenConferencier={onOpenConferencier}
            />
          ))}
        </div>
      )}
    </section>
  );
}

// One programme entry (conference or atelier/masterclass) — read-only card.
function ProgrammeCard({
  afficheUrl,
  badge,
  title,
  timeLabel,
  description,
  speakers,
  priceLabel,
  onOpenConferencier,
}: {
  afficheUrl: string | null;
  badge?: string | undefined;
  title: string;
  timeLabel: string;
  description?: string | undefined;
  speakers: EvenementConferencier[];
  priceLabel?: string | undefined;
  onOpenConferencier: (c: EvenementConferencier) => void;
}) {
  return (
    <article
      style={{
        display: 'flex',
        gap: 14,
        padding: '14px 18px',
        background: 'white',
        border: '1px solid rgba(15,25,22,0.08)',
        borderRadius: 12,
      }}
    >
      <AfficheThumbnail url={afficheUrl} alt={title} width={80} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          {badge && (
            <span
              style={{
                fontFamily: 'var(--mtc-font-mono)',
                fontSize: 9,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: '#9E7508',
                border: '0.5px solid #C9A961',
                borderRadius: 4,
                padding: '1px 6px',
              }}
            >
              {badge}
            </span>
          )}
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0F1916' }}>{title}</div>
        </div>
        <div style={{ fontSize: 12, color: 'rgba(15,25,22,0.6)', marginTop: 3 }}>{timeLabel}</div>
        {description && (
          <p style={{ fontSize: 13, color: 'rgba(15,25,22,0.75)', margin: '8px 0 0', lineHeight: 1.5 }}>
            {description}
          </p>
        )}
        {speakers.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
            <span
              style={{
                fontFamily: 'var(--mtc-font-mono)',
                fontSize: 10,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: 'rgba(15,25,22,0.45)',
              }}
            >
              Avec
            </span>
            {speakers.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onOpenConferencier(c)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '3px 9px 3px 3px',
                  background: 'rgba(11,93,59,0.08)',
                  border: '1px solid rgba(11,93,59,0.2)',
                  borderRadius: 999,
                  color: 'var(--mtc-primary, #0B5D3B)',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                {c.photoUrl ? (
                  <img
                    src={c.photoUrl}
                    alt=""
                    style={{ width: 20, height: 20, borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <span
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: 'var(--mtc-primary, #0B5D3B)',
                      color: 'white',
                      display: 'inline-grid',
                      placeItems: 'center',
                      fontSize: 10,
                      fontWeight: 700,
                    }}
                  >
                    {(c.name?.[0] ?? '?').toUpperCase()}
                  </span>
                )}
                {c.name}
              </button>
            ))}
          </div>
        )}
      </div>
      {priceLabel && (
        <div
          style={{
            fontFamily: 'var(--mtc-font-display)',
            fontSize: 15,
            color: 'var(--mtc-primary, #0B5D3B)',
            whiteSpace: 'nowrap',
          }}
        >
          {priceLabel}
        </div>
      )}
    </article>
  );
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div
        style={{
          fontFamily: 'var(--mtc-font-mono)',
          fontSize: 10,
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          color: 'rgba(15,25,22,0.5)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, color: '#0F1916' }}>{value}</div>
    </div>
  );
}

function formatDateRange(startIso: string, endIso: string): string {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime())) return '';
  const fmt = (d: Date) =>
    d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
  if (Number.isNaN(end.getTime()) || start.toDateString() === end.toDateString()) {
    return fmt(start);
  }
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()}–${end.getDate()} ${end.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}`;
  }
  return `${fmt(start)} → ${fmt(end)}`;
}
